import React from "react";
import {
    Typography,
    Box,
    Card,
    Chip,
    Avatar,
    Stack,
    Fade,
    Paper,
    useTheme,
    alpha
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const EMPTY_VALUE = "Não definido";

const toComparablePhotoList = (value) => {
    if (!Array.isArray(value)) return [];

    return value
        .filter(Boolean)
        .map((item) => String(item).trim())
        .filter(Boolean)
        .sort();
};

const toComparableValue = (field, value) => {
    if (field === "photosTaken") return toComparablePhotoList(value);
    if (value === null || value === undefined) return "";
    return String(value).trim();
};

const valuesAreEqual = (field, oldValue, newValue) => {
    const oldComparable = toComparableValue(field, oldValue);
    const newComparable = toComparableValue(field, newValue);
    return JSON.stringify(oldComparable) === JSON.stringify(newComparable);
};

const isImageValue = (value) => {
    if (typeof value !== "string") return false;
    return (
        value.startsWith("data:image/") ||
        value.startsWith("/uploads/") ||
        /^https?:\/\//i.test(value)
    );
};

// Modern styles using MUI's theming system
const ChangeDisplay = ({ pendingChanges, originalValues }) => {
    const theme = useTheme();

    const styles = {
        changeCard: {
            margin: "16px 0",
            borderRadius: "16px",
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            overflow: "hidden",
            position: "relative",
            transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
            "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.18)}`
            }
        },
        header: {
            display: "flex",
            alignItems: "center",
            padding: "16px 20px",
            background: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        },
        fieldName: {
            marginLeft: 10,
            fontWeight: 600,
            color: theme.palette.primary.main,
            letterSpacing: "0.5px",
            fontSize: "1rem"
        },
        contentContainer: {
            padding: "20px",
        },
        valueContainer: {
            padding: "16px",
            borderRadius: "12px",
            marginBottom: "8px",
            transition: "all 0.2s ease"
        },
        oldValue: {
            background: alpha(theme.palette.error.light, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
        },
        newValue: {
            background: alpha(theme.palette.success.light, 0.1),
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
        },
        valueHeading: {
            display: "flex",
            alignItems: "center",
            marginBottom: "8px"
        },
        valueLabel: {
            fontSize: "0.825rem",
            fontWeight: 500,
            marginLeft: "8px"
        },
        valueContent: {
            fontSize: "0.95rem",
            lineHeight: 1.6,
            paddingLeft: "28px" // Aligned with icon
        },
        oldValueText: {
            color: theme.palette.text.secondary,
            fontStyle: "italic",
            textDecoration: "line-through",
            textDecorationColor: alpha(theme.palette.error.main, 0.4),
            textDecorationThickness: "1px"
        },
        newValueText: {
            color: theme.palette.text.primary,
            fontWeight: 500
        },
        noChangesText: {
            color: theme.palette.text.secondary,
            fontStyle: "italic",
            textAlign: "center",
            padding: "24px 0"
        },
        changeIndicator: {
            position: "absolute",
            top: 0,
            right: 0,
            borderRadius: "0 0 0 12px",
            padding: "4px 12px",
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: "0.5px",
            textTransform: "uppercase"
        }
    };

    const safePendingChanges = pendingChanges || {};
    const safeOriginalValues = originalValues || {};
    const fieldsToCompare = ["obs", "photosTaken"];
    const changes = fieldsToCompare.filter((field) =>
        Object.prototype.hasOwnProperty.call(safePendingChanges, field) &&
        !valuesAreEqual(field, safeOriginalValues[field], safePendingChanges[field])
    );

    // Format field name for display
    const formatFieldName = (field) => {
        const fieldMap = {
            obs: "Observações",
            photosTaken: "Fotos Registradas"
        };
        return fieldMap[field] || field.charAt(0).toUpperCase() + field.slice(1);
    };

    const renderPhotoValue = (value, variant) => {
        const photos = Array.isArray(value) ? value.filter(Boolean) : [];

        if (photos.length === 0) {
            return (
                <Typography sx={{ ...styles.valueContent, ...(variant === "old" ? styles.oldValueText : styles.newValueText) }}>
                    Sem fotos
                </Typography>
            );
        }

        return (
            <Box sx={{ pl: "28px" }}>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                    {photos.map((photo, index) => (
                        isImageValue(photo) ? (
                            <Box
                                key={`${photo}-${index}`}
                                component="a"
                                href={photo}
                                target="_blank"
                                rel="noreferrer"
                                sx={{
                                    display: "block",
                                    width: 96,
                                    height: 96,
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                                    backgroundColor: alpha(theme.palette.background.default, 0.7),
                                }}
                            >
                                <Box
                                    component="img"
                                    src={photo}
                                    alt={`Foto ${index + 1}`}
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                            </Box>
                        ) : (
                            <Chip
                                key={`${photo}-${index}`}
                                label={`Foto ${index + 1}`}
                                variant="outlined"
                                size="small"
                                sx={{ maxWidth: 180 }}
                            />
                        )
                    ))}
                </Stack>
            </Box>
        );
    };

    const renderValue = (field, value, variant) => {
        if (field === "photosTaken") return renderPhotoValue(value, variant);

        return (
            <Typography sx={{ ...styles.valueContent, ...(variant === "old" ? styles.oldValueText : styles.newValueText) }}>
                {value || EMPTY_VALUE}
            </Typography>
        );
    };

    if (changes.length === 0) {
        return (
            <Fade in={true} timeout={800}>
                <Box sx={{ padding: "20px" }}>
                    <Paper
                        elevation={0}
                        sx={{
                            padding: "32px 20px",
                            borderRadius: "16px",
                            background: alpha(theme.palette.background.default, 0.7),
                            border: `1px dashed ${alpha(theme.palette.text.secondary, 0.2)}`
                        }}
                    >
                        <Stack
                            direction="column"
                            spacing={2}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <CompareArrowsIcon
                                sx={{
                                    fontSize: 48,
                                    color: alpha(theme.palette.text.secondary, 0.5),
                                    opacity: 0.7
                                }}
                            />
                            <Typography sx={styles.noChangesText}>
                                Não foram detectadas alterações nos campos monitorados
                            </Typography>
                            <Chip
                                label="Sem modificações"
                                color="default"
                                size="small"
                                variant="outlined"
                            />
                        </Stack>
                    </Paper>
                </Box>
            </Fade>
        );
    }

    return (
        <Stack spacing={3} sx={{ padding: "8px 0" }}>
            {changes.map((field, index) => {
                const oldValue = safeOriginalValues[field];
                const newValue = safePendingChanges[field];

                return (
                    <Fade
                        in={true}
                        timeout={500 + index * 200}
                        key={field}
                    >
                        <Card sx={styles.changeCard}>
                            <Box sx={styles.header}>
                                <Avatar
                                    sx={{
                                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                                        color: theme.palette.primary.main,
                                        width: 36,
                                        height: 36
                                    }}
                                >
                                    <EditNoteIcon fontSize="small" />
                                </Avatar>
                                <Typography sx={styles.fieldName}>
                                    {formatFieldName(field)}
                                </Typography>
                                <Box sx={styles.changeIndicator}>
                                    Modificado
                                </Box>
                            </Box>

                            <Box sx={styles.contentContainer}>
                                <Paper
                                    elevation={0}
                                    sx={{ ...styles.valueContainer, ...styles.oldValue }}
                                >
                                    <Box sx={styles.valueHeading}>
                                        <Avatar
                                            sx={{
                                                bgcolor: alpha(theme.palette.error.main, 0.15),
                                                color: theme.palette.error.main,
                                                width: 20,
                                                height: 20
                                            }}
                                        >
                                            <CompareArrowsIcon sx={{ fontSize: 14 }} />
                                        </Avatar>
                                        <Typography
                                            sx={styles.valueLabel}
                                            color="error.main"
                                        >
                                            Valor anterior
                                        </Typography>
                                    </Box>
                                    {renderValue(field, oldValue, "old")}
                                </Paper>

                                <Paper
                                    elevation={0}
                                    sx={{ ...styles.valueContainer, ...styles.newValue }}
                                >
                                    <Box sx={styles.valueHeading}>
                                        <Avatar
                                            sx={{
                                                bgcolor: alpha(theme.palette.success.main, 0.15),
                                                color: theme.palette.success.main,
                                                width: 20,
                                                height: 20
                                            }}
                                        >
                                            <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                                        </Avatar>
                                        <Typography
                                            sx={styles.valueLabel}
                                            color="success.main"
                                        >
                                            Novo valor
                                        </Typography>
                                    </Box>
                                    {renderValue(field, newValue, "new")}
                                </Paper>
                            </Box>
                        </Card>
                    </Fade>
                );
            })}
        </Stack>
    );
};

export default ChangeDisplay;
